import { useTranslation } from 'react-i18next';
import type { DocumentDirectoryType, ObjectProposalGenerationType } from '@econtractor/types';
import { GenerationLogCommentCell } from 'modules/templates';
import { UserNameItem } from 'modules/user';
import { format$Price, formatPrice } from 'utils/helpers/functions/formatPrice';
import { withLocalCurrency } from 'utils/helpers/currency/currencyHelpers';
import { safeFloatParse } from 'utils/helpers/safeParsing';
import CopyTextWrapper from 'components/others/CopyTextWrapper';
import PercentPie from 'components/PercentPie';
import DocumentActions from './DocumentActions';

const DOCUMENT_TYPE_BADGE_CLASS: Record<string, string> = {
    contract: 'bg-light-primary text-primary',
    invoice: 'bg-light-info text-info',
    act: 'bg-light-success text-success',
    other: 'bg-light-secondary text-secondary',
    proposal: 'bg-light-warning text-warning',
};

const formatGenerationAmount = (amount: number, isUsd: boolean) => {
    const value = safeFloatParse(amount || 0);
    if (isUsd) return format$Price(value);
    return value >= 0
        ? withLocalCurrency(formatPrice(value))
        : withLocalCurrency(formatPrice(-value), true);
};

export interface DocumentTableRowProps {
    row: ObjectProposalGenerationType;
    deletingId: number | null;
    onOpenDocument: (row: ObjectProposalGenerationType) => void;
    onCreateFromGeneration: (row: ObjectProposalGenerationType) => void;
    onToWarehouse: (row: ObjectProposalGenerationType) => void;
    onPayment: (row: ObjectProposalGenerationType) => void;
    onDelete: (row: ObjectProposalGenerationType) => void;
    onSendEmail: (row: ObjectProposalGenerationType) => void;
}

const DocumentTableRow = ({
    row,
    deletingId,
    onOpenDocument,
    onCreateFromGeneration,
    onToWarehouse,
    onPayment,
    onDelete,
    onSendEmail,
}: DocumentTableRowProps) => {
    const { t } = useTranslation(['Objects', 'Document', 'Proposal']);
    const isDeleted = Boolean(Number(row.deleted || 0));
    const isUsd = Boolean(row.proposalUsd);
    const typeKey = (row.proposalType || 'other') as DocumentDirectoryType | 'proposal';
    const badgeClass = DOCUMENT_TYPE_BADGE_CLASS[typeKey] || DOCUMENT_TYPE_BADGE_CLASS.other;
    const typeLabel = t(`Document:documentTypes.${typeKey}`, {
        defaultValue: typeKey,
    });
    const paidAmount = safeFloatParse(row.proposalPay || 0);
    const totalAmount = safeFloatParse(row.proposalAmount || 0);
    const paidPercent = totalAmount > 0 ? paidAmount / totalAmount * 100 : 0;
    const name = row.proposalName || '-';
    const description = String(row.generationComment || '').trim();
    const number = String(row.proposalNumber || '').trim();

    return (
        <tr className={isDeleted ? 'is-deleted' : undefined}>
            <td className="name-cell cursor-pointer" onClick={() => onOpenDocument(row)}>
                <h6 className="mb-1 text-break">{name}</h6>
                <p className="text-muted f-12 mb-0 my-1 text-truncate" title={description}>
                    {description || '-'}
                </p>
            </td>
            <td className="number-cell">
                {number ? (
                    <CopyTextWrapper value={number}>
                        <span title={number}>{number}</span>
                    </CopyTextWrapper>
                ) : (
                    <span className="text-muted">-</span>
                )}
            </td>
            <td className="type-cell">
                <span className={`badge text-truncate ${badgeClass}`} title={typeLabel}>
                    {typeLabel}
                </span>
            </td>
            <td className="user-cell">
                <UserNameItem
                    smallMode
                    user={{
                        ID: row.ownerId,
                        userName: row.userName || `#${row.ownerId}`,
                        userEmail: row.userEmail || '',
                        userPicture: row.userPicture || '',
                    }}
                />
            </td>
            <td className="amount-cell text-end">
                <div className="d-flex flex-column align-items-end gap-1">
                    <PercentPie percent={paidPercent} mode="payment" />
                    <div>{formatGenerationAmount(row.proposalAmount, isUsd)}</div>
                </div>
                {paidAmount !== 0 ? (
                    <div className="text-success fs-12">
                        {formatGenerationAmount(paidAmount || 0, isUsd)}
                    </div>
                ) : (
                    <div className="text-muted fs-12">-</div>
                )}
            </td>
            <td className="comment-cell">
                <GenerationLogCommentCell
                    generationComment={row.generationComment}
                    proposalEmailBody={row.proposalEmailBody}
                    generationTitle={t('Proposal:edit.generate.logCommentHint')}
                    emailBodyTitle={t('Proposal:edit.generate.logEmailBodyHint')}
                />
            </td>
            <td className="actions">
                <DocumentActions
                    row={row}
                    deletingId={deletingId}
                    onCreateFromGeneration={onCreateFromGeneration}
                    onToWarehouse={onToWarehouse}
                    onPayment={onPayment}
                    onDelete={onDelete}
                    onSendEmail={onSendEmail}
                />
            </td>
        </tr>
    );
};

export default DocumentTableRow;
